import { useState } from 'react';
import { toast } from "@/hooks/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Review } from '@/types';

interface UseReviewActionsProps {
  onRefreshReviews: () => void;
  // Add other dependencies as needed, e.g., for modals
  setIsViewingReviewDetails: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedReview: React.Dispatch<React.SetStateAction<Review | null>>;
  setShowDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setReviewToDelete: React.Dispatch<React.SetStateAction<Review | null>>;
}

const useReviewActions = ({
  onRefreshReviews,
  setIsViewingReviewDetails,
  setSelectedReview,
  setShowDeleteConfirmation,
  setReviewToDelete,
}: UseReviewActionsProps) => {

  // Helper function to delete related funnel events using session_id
  const deleteRelatedFunnelEvents = async (review: Review) => {
    try {
      // If the review has a session_id, use it for precise matching
      if (review.session_id) {
        const { error: funnelError } = await (supabase as any)
          .from('review_funnel_events')
          .delete()
          .eq('session_id', review.session_id);

        if (funnelError) {
          console.error("Error deleting funnel events by session_id:", funnelError);
          // Don't throw here - we still want to delete the review even if funnel cleanup fails
        } else {
          console.log("Successfully deleted funnel events for session:", review.session_id);
        }
      } else {
        // Fallback to old logic for reviews without session_id (legacy reviews)
        console.log("No session_id found for review:", review.id, "- using fallback time-based matching");
        
        const reviewTime = new Date(review.created_at);
        const startTime = new Date(reviewTime.getTime() - 30 * 60 * 1000); // 30 minutes before
        const endTime = new Date(reviewTime.getTime() + 30 * 60 * 1000); // 30 minutes after

        const deleteQuery = (supabase as any)
          .from('review_funnel_events')
          .delete()
          .eq('business_id', review.business_id)
          .gte('timestamp', startTime.toISOString())
          .lte('timestamp', endTime.toISOString());

        // If the review has a tag_id, also filter by that
        if (review.tag_id) {
          deleteQuery.eq('tag_id', review.tag_id);
        }

        const { error: funnelError } = await deleteQuery;

        if (funnelError) {
          console.error("Error deleting funnel events by time/business/tag:", funnelError);
        } else {
          console.log("Successfully deleted funnel events using fallback method for review:", review.id);
        }
      }
    } catch (error) {
      console.error("Error in deleteRelatedFunnelEvents:", error);
      // Don't throw - continue with review deletion
    }
  };

  // Helper function to delete funnel events for multiple reviews
  const deleteBulkRelatedFunnelEvents = async (reviews: Review[]) => {
    try {
      // Separate reviews with session_id from those without
      const reviewsWithSession = reviews.filter(r => r.session_id);
      const reviewsWithoutSession = reviews.filter(r => !r.session_id);

      // Delete funnel events using session_id for precise matching
      if (reviewsWithSession.length > 0) {
        const sessionIds = reviewsWithSession.map(r => r.session_id!);
        const { error: sessionError } = await (supabase as any)
          .from('review_funnel_events')
          .delete()
          .in('session_id', sessionIds);

        if (sessionError) {
          console.error("Error deleting bulk funnel events by session_id:", sessionError);
        } else {
          console.log(`Successfully deleted funnel events for ${sessionIds.length} sessions`);
        }
      }

      // Fallback to time-based deletion for legacy reviews without session_id
      if (reviewsWithoutSession.length > 0) {
        console.log(`Using fallback time-based deletion for ${reviewsWithoutSession.length} legacy reviews`);
        
        const businessIds = [...new Set(reviewsWithoutSession.map(r => r.business_id))];
        const oldestReview = reviewsWithoutSession.reduce((oldest, current) => 
          new Date(oldest.created_at) < new Date(current.created_at) ? oldest : current
        );
        const newestReview = reviewsWithoutSession.reduce((newest, current) => 
          new Date(newest.created_at) > new Date(current.created_at) ? newest : current
        );

        const startTime = new Date(new Date(oldestReview.created_at).getTime() - 30 * 60 * 1000);
        const endTime = new Date(new Date(newestReview.created_at).getTime() + 30 * 60 * 1000);

        const { error: timeError } = await (supabase as any)
          .from('review_funnel_events')
          .delete()
          .in('business_id', businessIds)
          .gte('timestamp', startTime.toISOString())
          .lte('timestamp', endTime.toISOString());

        if (timeError) {
          console.error("Error deleting bulk funnel events by time:", timeError);
        } else {
          console.log("Successfully deleted bulk funnel events using fallback method");
        }
      }
    } catch (error) {
      console.error("Error in deleteBulkRelatedFunnelEvents:", error);
    }
  };

  const handleViewDetails = (review: Review) => {
    setSelectedReview(review);
    setIsViewingReviewDetails(true);
    console.log("View details for review:", review.id);
    // TODO: Implement view details modal/component
  };

  const handleDeleteReview = (review: Review) => {
    setReviewToDelete(review);
    setShowDeleteConfirmation(true);
    console.log("Prepare to delete review:", review.id);
  };

  const confirmDeleteReview = async (review: Review) => {
    console.log("Confirming delete for review:", review.id);
    toast.info(`Attempting to delete review ${review.id}...`);
    try {
      // First, delete related funnel events
      await deleteRelatedFunnelEvents(review);

      // Then delete the review
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', review.id);

      if (error) {
        throw error;
      }

      // Refresh the reviews data from the parent component
      onRefreshReviews();
      toast.success(`Review ${review.id} deleted successfully.`);

    } catch (error) {
      console.error("Error deleting review:", error);
      if (error instanceof Error) {
        toast.error("Failed to delete review.", {
          description: error instanceof Error ? error.message : String(error),
        });
      } else {
        toast.error("Failed to delete review.");
      }
    }
  };

  const confirmBulkDeleteReviews = async (reviewIds: string[]) => {
    console.log("Confirming bulk delete for reviews:", reviewIds);
    toast.info(`Attempting to delete ${reviewIds.length} reviews...`);
    try {
      // First, get the full review data for funnel cleanup
      const { data: reviewsToDelete, error: fetchError } = await supabase
        .from('reviews')
        .select('*')
        .in('id', reviewIds);

      if (fetchError) {
        throw fetchError;
      }

      if (reviewsToDelete && reviewsToDelete.length > 0) {
        // Delete related funnel events
        await deleteBulkRelatedFunnelEvents(reviewsToDelete as any);
      }

      // Then delete the reviews
      const { error } = await supabase
        .from('reviews')
        .delete()
        .in('id', reviewIds);

      if (error) {
        throw error;
      }

      // Refresh the reviews data from the parent component
      onRefreshReviews();
      toast.success(`${reviewIds.length} reviews deleted successfully.`);

    } catch (error) {
      console.error("Error deleting reviews:", error);
      if (error instanceof Error) {
        toast.error("Failed to delete reviews.", {
          description: error instanceof Error ? error.message : String(error),
        });
      } else {
        toast.error("Failed to delete reviews.");
      }
    }
  };

  return {
    handleViewDetails,
    handleDeleteReview,
    confirmDeleteReview,
    confirmBulkDeleteReviews,
  };
};

export { useReviewActions };
